import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Checkbox,
  Collapse,
  FormControlLabel,
  LinearProgress,
  TextField,
} from "@mui/material";
import moment from "moment";
import { useEffect } from "react";
import { useState } from "react";
import { DownloadLinkEnh } from "../../../../components/common/DownloadLink/DownloadLinkEnh";
import {
  SimpleCardContainer,
  SimpleCardContent,
  SimpleCardTitle,
} from "../../../../components/common/SimpleCard/SimpleCard";
import FileUploadField from "../../../../components/forms/FileUploadField";
import Auth from "../../../../services/Auth/Auth";
import FilesService from "../../../../services/Integration/files/FilesService";
import { BerkasSidangService } from "../../../../services/Integration/mahasiswa-sidang/BerkasSidangService";
import { DeadlineSubmisi } from "../../../../utils/constants/DeadlineSubmisi.constants";
import { toTitleCase } from "../../../../utils/helpers/common.helpers";
import { FileHelpers } from "../../../../utils/helpers/File.helpers";
import BerkasFinalSubmittedFile from "./BerkasFinalSubmittedFile";
import { KODE_STATUS_TYPE } from "../../../../utils/constants/StatusType.constants";
import { useNavigate } from "react-router-dom";

const BERKAS_FINAL_KEY = "berkas_final";
const SOURCE_CODE_KEY = "source_code";
const UPLOADED_FILE_INITIAL = {
  [BERKAS_FINAL_KEY]: null,
  [SOURCE_CODE_KEY]: null,
};
const ALERT_INITIAL = { success: false, fail: false };
const BERKAS_FINAL_DETAILS_INITIAL = {
  judul_final: "",
  judul_final_english: "",
  abstrak: "",
  abstrak_english: "",
  kata_kunci: "",
};
const BERKAS_FINAL_DEADLINE_KEY = DeadlineSubmisi.jenisSubmisi["Berkas Final"];
const BERKAS_FINAL_SOFTCOPY_DEADLINE_KEY =
  DeadlineSubmisi.jenisBerkas["Softcopy Berkas"];

const STATUS_VALID_KEY = [KODE_STATUS_TYPE.MENUNGGU_BERKAS_AKHIR, KODE_STATUS_TYPE.SELESAI_MK_SPESIAL]

export default function BerkasFinal({ mKSpesialId, deadlines, kodeStatus }) {
  const navigate = useNavigate();
  const user = Auth.getCurrentUser();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const getBerkasFinalDeadline = () => {
    const berkasFinalDeadline = deadlines.filter(
      (deadline) =>
        deadline.jenis_submisi === BERKAS_FINAL_DEADLINE_KEY &&
        deadline.jenis_berkas === BERKAS_FINAL_SOFTCOPY_DEADLINE_KEY
    );
    return berkasFinalDeadline.length === 0 ? null : berkasFinalDeadline[0];
  };

  const berkasFinalSubmissionDeadline = getBerkasFinalDeadline();
  const isStatusValid = STATUS_VALID_KEY.includes(kodeStatus)
  const deadlineMoment =
    berkasFinalSubmissionDeadline !== null
      ? moment(
          `${berkasFinalSubmissionDeadline.tanggal} ${berkasFinalSubmissionDeadline.jam}`,
          "YYYY-MM-DD HH:mm:ss"
        )
      : null;
  const [isLoading, setIsLoading] = useState({
    form: false,
    submit: false,
  });
  const [showAlert, setShowAlert] = useState(ALERT_INITIAL);
  const [berkasFinalDetails, setBerkasFinalDetails] = useState(
    BERKAS_FINAL_DETAILS_INITIAL
  );
  const [uploadedFiles, setUploadedFiles] = useState(UPLOADED_FILE_INITIAL);
  const [lembarPengesahanChecked, setLembarPangesahanChecked] = useState(false);

  useEffect(() => {
    fetchBerkasFinal();
  }, []);

  const fetchBerkasFinal = async () => {
    setIsLoading({ ...isLoading, form: true });
    await BerkasSidangService.getBerkasFinalByMkSpesialId(mKSpesialId)
      .then((response) => {
        if (response.status === 200) {
          const data = response.data;
          if (data.length === 0) {
            return;
          }
          setHasSubmitted(true);
          setBerkasFinalDetails(data[0]);
        } else {
          console.log(response);
        }
      })
      .catch((error) => {
        console.log(error);
      });
    setIsLoading({ ...isLoading, form: false });
  };

  const handleChange = (e) =>
    setBerkasFinalDetails({
      ...berkasFinalDetails,
      [e.target.name]: e.target.value,
    });

  const handleFileUpload = (e) => {
    if (!e.target.files) {
      return;
    }
    const uploadedFile = e.target.files[0];
    setUploadedFiles({ ...uploadedFiles, [e.target.name]: uploadedFile });
  };

  const resetUploadedFile = (uploadedFileKey) => {
    setUploadedFiles({ ...uploadedFiles, [uploadedFileKey]: null });
  };

  const getFilePrefix = (jenis) =>
    `${toTitleCase(jenis.replace("_", " ")).replace(" ", "")}_${mKSpesialId}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading({ ...isLoading, submit: true });
    setShowAlert(ALERT_INITIAL);

    let fileBase64 = {};
    let fileUploadResponseData = {};

    // Simpan file yang diunggah di server
    await Promise.all(
      Object.entries(uploadedFiles).map(async (file) => {
        const jenisFile = file[0];
        const fileObj = file[1];
        if (!fileObj) return;

        await FileHelpers.fileToBase64(fileObj)
          .then(
            (result) => (fileBase64 = { ...fileBase64, [jenisFile]: result })
          )
          .catch((error) => console.log(error));

        fileUploadResponseData[jenisFile] = await FilesService.uploadFile({
          userId: user.id,
          file: fileBase64[jenisFile],
          prefix: getFilePrefix(jenisFile),
          mKSpesialId: mKSpesialId,
        })
          .then((response) => {
            if (response.status === 201) {
              return response.data;
            } else {
              setShowAlert({ ...showAlert, fail: true });
              return null;
            }
          })
          .catch((error) => {
            console.log(error);
            setShowAlert({ ...showAlert, fail: true });
            return null;
          });
      })
    );

    // Simpan berkas final as object
    const payload = {
      judul_final: berkasFinalDetails.judul_final,
      judul_final_english: berkasFinalDetails.judul_final_english,
      abstrak: berkasFinalDetails.abstrak,
      abstrak_english: berkasFinalDetails.abstrak_english,
      kata_kunci: berkasFinalDetails.kata_kunci,
      mk_spesial: parseInt(mKSpesialId),
    };

    if (fileUploadResponseData[BERKAS_FINAL_KEY]) {
      payload.berkas = fileUploadResponseData[BERKAS_FINAL_KEY].id;
      payload.tipe_berkas = "Softcopy";
    }

    await BerkasSidangService.uploadBerkasFinal(payload)
      .then(async (response) => {
        if (response.status === 200) {
          setShowAlert({ ...showAlert, success: true });
        } else {
          setShowAlert({ ...showAlert, fail: true });
        }
      })
      .catch((error) => {
        console.log(error);
        setShowAlert({ ...showAlert, fail: true });
      });

    setTimeout(() => navigate(0), 3000);  //refresh page
    setIsLoading({ ...isLoading, submit: false });
  };

  return (
    <div className="mt-4">
      {isLoading.form ? (
        <LinearProgress />
      ) : (
        <>
          {isStatusValid ? (
              <>
                {hasSubmitted ? (
                  <BerkasFinalSubmittedFile
                    mKSpesialId={mKSpesialId}
                    berkas={berkasFinalDetails}
                  />
                ) : (
                  <>
                    <div>
                      <span className="font-bold">Deadline pengumpulan: </span>
                      <span className="font-bold text-red-500">
                        {berkasFinalSubmissionDeadline !== null
                          ? `${moment(deadlineMoment).format("DD/MM/YYYY HH:mm")} WIB`
                          : "-"}
                      </span>
                    </div>
                    {berkasFinalSubmissionDeadline !== null && (
                      <small className="text-red-500">
                        * Pastikan bahwa berkas yang anda kumpulkan merupakan versi
                        FINAL karena submisi hanya dapat dilakukan <b>SATU KALI</b>.
                      </small>
                    )}
                    {berkasFinalSubmissionDeadline !== null &&
                    deadlineMoment.isAfter(moment()) ? (
                      <div className="mb-4">
                        <SimpleCardContainer>
                          <SimpleCardTitle>Lembar Pengesahan</SimpleCardTitle>
                          <SimpleCardContent>
                            <div>
                              Anda{" "}
                              <span className="font-bold text-red-500">wajib</span>{" "}
                              melampirkan lembar pengesahan{" "}
                              <DownloadLinkEnh
                                endpoint={`/file/lembar-pengesahan?mk_spesial=${mKSpesialId}`}
                                label={"berikut"}
                              />{" "}
                              pada berkas final
                            </div>
                            <div>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={lembarPengesahanChecked}
                                    onChange={(e) =>
                                      setLembarPangesahanChecked(e.target.checked)
                                    }
                                  />
                                }
                                label={
                                  "Saya sudah menambahkan lembar pengesahan ke dalam berkas final saya."
                                }
                              />
                            </div>
                          </SimpleCardContent>
                        </SimpleCardContainer>
                      </div>
                    ) : (
                      <div className="text-center pt-3">
                        Harap hubungi sekretariat untuk menetapkan deadline terlebih
                        dahulu
                      </div>
                    )}
                    <form onSubmit={handleSubmit}>
                      {berkasFinalSubmissionDeadline !== null &&
                      deadlineMoment.isBefore(moment()) ? (
                        <span className="font-bold text-center">{`Submisi Berkas Final telah melewati batas deadline pengumpulan sehingga anda tidak dapat lagi melakukan submisi berkas!`}</span>
                      ) : (
                        <Collapse in={lembarPengesahanChecked}>
                          <div className="flex flex-col gap-4">
                            <SimpleCardContainer>
                              <SimpleCardTitle>
                                Berkas Tugas Akhir/Tesis/Disertasi
                              </SimpleCardTitle>
                              <SimpleCardContent>
                                <div className="flex flex-col gap-4">
                                  <TextField
                                    name="judul_final"
                                    value={berkasFinalDetails.judul_final}
                                    onChange={handleChange}
                                    inputProps={{ maxLength: 300 }}
                                    label="Judul Final (Bahasa Indonesia, maksimal 300 karakter)"
                                    required
                                    multiline
                                    fullWidth
                                  />
                                  <TextField
                                    name="abstrak"
                                    value={berkasFinalDetails.abstrak}
                                    onChange={handleChange}
                                    label="Abstrak (Bahasa Indonesia, maksimal 4000 karakter)"
                                    required
                                    inputProps={{ maxLength: 4000 }}
                                    multiline
                                    fullWidth
                                  />
                                  <TextField
                                    name="judul_final_english"
                                    value={berkasFinalDetails.judul_final_english}
                                    onChange={handleChange}
                                    label="Judul Final (English, maksimal 300 karakter)"
                                    required
                                    inputProps={{ maxLength: 300 }}
                                    multiline
                                    fullWidth
                                  />
                                  <TextField
                                    name="abstrak_english"
                                    value={berkasFinalDetails.abstrak_english}
                                    onChange={handleChange}
                                    label="Abstrak (English, maksimal 4000 karakter)"
                                    required
                                    inputProps={{ maxLength: 4000 }}
                                    multiline
                                    fullWidth
                                  />
                                  <TextField
                                    name="kata_kunci"
                                    value={berkasFinalDetails.kata_kunci}
                                    onChange={handleChange}
                                    label="Kata Kunci (English, maksimal 4000 karakter)"
                                    required
                                    inputProps={{ maxLength: 300 }}
                                    multiline
                                    fullWidth
                                  />
                                  <FileUploadField
                                    title={"Berkas Final"}
                                    inputName={BERKAS_FINAL_KEY}
                                    acceptedFileTypes={".pdf,.zip"}
                                    onFileUpload={handleFileUpload}
                                    onResetUploadedFile={() =>
                                      resetUploadedFile(BERKAS_FINAL_KEY)
                                    }
                                    uploadedFile={uploadedFiles[BERKAS_FINAL_KEY]}
                                    required={true}
                                  />
                                </div>
                              </SimpleCardContent>
                            </SimpleCardContainer>
                            <SimpleCardContainer>
                              <SimpleCardTitle>
                                Source Code (jika ada)
                              </SimpleCardTitle>
                              <SimpleCardContent>
                                <FileUploadField
                                  title={"Source Code"}
                                  inputName={SOURCE_CODE_KEY}
                                  acceptedFileTypes={".zip"}
                                  onFileUpload={handleFileUpload}
                                  onResetUploadedFile={() =>
                                    resetUploadedFile(SOURCE_CODE_KEY)
                                  }
                                  uploadedFile={uploadedFiles[SOURCE_CODE_KEY]}
                                  required={false}
                                />
                              </SimpleCardContent>
                            </SimpleCardContainer>
                          </div>
                          <div className="mt-4 flex flex-col">
                            <Collapse in={showAlert.success}>
                              <Alert
                                onClose={() =>
                                  setShowAlert({ ...showAlert, success: false })
                                }
                              >
                                Berkas final berhasil diunggah!
                                Halaman akan ter-refresh dalam 3 detik.
                              </Alert>
                            </Collapse>
                            <Collapse in={showAlert.fail}>
                              <Alert
                                severity="error"
                                onClose={() =>
                                  setShowAlert({ ...showAlert, fail: false })
                                }
                              >
                                Terjadi permasalahan saat mengunggah. Silakan coba
                                beberapaa saat lagi.
                              </Alert>
                            </Collapse>
                            <Collapse in={showAlert.success || showAlert.fail}>
                              <div className="my-2" />
                            </Collapse>
                            <LoadingButton
                              loading={isLoading.submit}
                              variant="contained"
                              type="submit"
                            >
                              Simpan
                            </LoadingButton>
                          </div>
                        </Collapse>
                      )}
                    </form>
                  </>
                )}
              </>
            ) : (
            <div>
              <div>
                  <span className="font-bold">Deadline pengumpulan: </span>
                  <span className="font-bold text-red-500">
                    {berkasFinalSubmissionDeadline !== null
                      ? `${moment(deadlineMoment).format("DD/MM/YYYY HH:mm")} WIB`
                      : "-"}
                  </span>
                </div>
              <div className="w-full text-center font-bold">
                <div className="text-lg">Perhatian!</div>
                <div>
                  Seluruh dosen (termasuk yang tidak memberikan revisi) perlu menyetujui berkas revisi/sidang anda. Silakan menunggu
                  atau menghubungi dosen pembimbing atau penguji untuk menyetujui
                  berkas revisi/sidang terhadap laporan tugas akhir anda. 
                </div>
              </div>
            </div>
            )
          }
        </>
      )}
    </div>
  );
}
